import React, { useState, useEffect } from 'react';
import { FiEdit2, FiTrash2, FiPlus, FiSave, FiX } from 'react-icons/fi';
import { gsidaGalleryApi, fileUploadApi } from '../../api/phpApi';
import './AdminManage.css';

export default function AdminGallery() {
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [mediaPreview, setMediaPreview] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    media_type: 'photo',
    media_url: '',
    thumbnail_url: '',
    description: '',
    event_date: ''
  });

  // Fetch gallery items
  useEffect(() => {
    loadGallery();
  }, []);

  const loadGallery = async () => {
    try {
      setLoading(true);
      const data = await gsidaGalleryApi.getAll();
      setMediaItems(data || []);
    } catch (err) {
      setError('Failed to load gallery: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle media upload
  const handleMediaUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const url = await fileUploadApi.uploadImage(file, 'images', 'gallery');
      setFormData(prev => ({ ...prev, media_url: url }));
      setMediaPreview(url);
    } catch (err) {
      setError('Failed to upload media: ' + err.message);
    }
  };

  // Handle add/edit submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await gsidaGalleryApi.update(editingId, formData);
      } else {
        await gsidaGalleryApi.create(formData);
      }
      resetForm();
      loadGallery();
    } catch (err) {
      setError('Failed to save media: ' + err.message);
    }
  };

  // Handle edit
  const handleEdit = (item) => {
    setFormData(item);
    setMediaPreview(item.media_url);
    setEditingId(item.id);
    setIsFormOpen(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this media?')) {
      try {
        await gsidaGalleryApi.delete(id);
        loadGallery();
      } catch (err) {
        setError('Failed to delete media: ' + err.message);
      }
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '',
      media_type: 'photo',
      media_url: '',
      thumbnail_url: '',
      description: '',
      event_date: ''
    });
    setMediaPreview('');
    setEditingId(null);
    setIsFormOpen(false);
  };

  if (loading) return <div className="admin-loading">Loading gallery...</div>;

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Manage GSIDA Gallery</h2>
        <button 
          className="btn-primary btn-add"
          onClick={() => setIsFormOpen(true)}
        >
          <FiPlus /> Add Media
        </button>
      </div>

      {error && <div className="error-alert">{error}</div>}

      {/* Form Modal */}
      {isFormOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingId ? 'Edit Media' : 'Add New Media'}</h3>
              <button className="btn-close" onClick={resetForm}>
                <FiX />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="admin-form">
              <div className="form-group form-row full">
                <label>Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Media Type *</label>
                  <select
                    name="media_type"
                    value={formData.media_type}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="photo">Photo</option>
                    <option value="video">Video</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Event Date</label>
                  <input
                    type="date"
                    name="event_date"
                    value={formData.event_date}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-group form-row full">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Media File *</label>
                <input
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleMediaUpload}
                  required={!editingId}
                />
                {mediaPreview && (
                  <img src={mediaPreview} alt="Media Preview" className="image-preview" />
                )}
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  <FiSave /> Save Media
                </button>
                <button type="button" className="btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Gallery Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px', marginTop: '20px' }}>
        {mediaItems.length === 0 ? (
          <p style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#6b7280' }}>No media found</p>
        ) : (
          mediaItems.map(item => (
            <div key={item.id} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', backgroundColor: 'white' }}>
              <img src={item.media_url} alt={item.title} style={{ width: '100%', height: '200px', objectFit: 'cover' }} />
              <div style={{ padding: '15px' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '1rem' }}>{item.title}</h4>
                <p style={{ margin: '0 0 8px 0', fontSize: '0.85rem', color: '#6b7280' }}>{item.media_type}</p>
                <div className="actions" style={{ marginTop: '12px' }}>
                  <button
                    className="btn-icon btn-edit"
                    onClick={() => handleEdit(item)}
                    title="Edit"
                  >
                    <FiEdit2 />
                  </button>
                  <button
                    className="btn-icon btn-delete"
                    onClick={() => handleDelete(item.id)}
                    title="Delete"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
